import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Code, ConnectError } from '@connectrpc/connect'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'

import Layout from '@components/layout/Layout'
import { Tile } from '@components/tiles'
import BrandedLoader from '@components/loaders/BrandedLoader'
import Button from '@components/buttons/Button'
import Modal from '@/pages/form/Modal'
import InvitationResultModal from './InvitationResultModal'
import type { InvitationResult } from './InvitationResultModal'
import { FormSubmissionSummary } from '@/api/brokers/v1/brokers_pb'
import { useBrokers } from '@/providers/brokerProvider'
import { useSessionTokenVerification } from '@/hooks/useSessionToken'

import './FormAdmin.css'

interface CreateFormCardProps {
	onClick: () => void
}

function CreateFormCard(props: CreateFormCardProps) {
	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Enter' && event.key !== ' ') return
		event.preventDefault()
		props.onClick()
	}

	return (
		<div
			className='createFormCard'
			onClick={props.onClick}
			onKeyDown={handleKeyDown}
			role='button'
			tabIndex={0}
		>
			<span className='createFormCardTitle'>
				Créer un nouveau
				<br />
				formulaire
			</span>
			<FontAwesomeIcon className='createFormCardPlus' icon={faPlus} />
		</div>
	)
}

// normalize makes the search accent-insensitive. NFD decomposition + stripping
// the combining marks handles every accented letter (é→e, à→a, ç→c, ü→u, ...);
// ligatures don't decompose, so they are expanded explicitly (œ→oe, æ→ae).
function normalize(value: string): string {
	return value
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/œ/g, 'oe')
		.replace(/æ/g, 'ae')
}

// EmptyFormsCard is the full-width call-to-action shown when the broker has no
// forms yet.
function EmptyFormsCard(props: CreateFormCardProps) {
	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Enter' && event.key !== ' ') return
		event.preventDefault()
		props.onClick()
	}

	return (
		<div
			className='createFormEmpty'
			onClick={props.onClick}
			onKeyDown={handleKeyDown}
			role='button'
			tabIndex={0}
		>
			<FontAwesomeIcon className='createFormEmptyPlus' icon={faPlus} />
			<span className='createFormEmptyTitle'>Créer un nouveau formulaire</span>
		</div>
	)
}

// matchesQuery returns true when the form matches a free-text search across its
// program name, city and buyer.
function matchesQuery(form: FormSubmissionSummary, query: string): boolean {
	const needle = normalize(query.trim())
	if (needle === '') return true
	const haystack = normalize(
		[form.projectName, form.city, form.buyerFirstName, form.buyerLastName].join(' '),
	)
	return haystack.includes(needle)
}

function FormAdmin() {
	const [forms, setForms] = useState<FormSubmissionSummary[]>([])
	const [recentForms, setRecentForms] = useState<FormSubmissionSummary[]>([])
	// A search typed from another page lands here through `?q=`.
	const [searchParams] = useSearchParams()
	const [query, setQuery] = useState(() => searchParams.get('q') ?? '')
	// Resending is a two-step flow: confirm first (it mails real people), then
	// report what actually went out.
	const [resendTarget, setResendTarget] = useState<FormSubmissionSummary | null>(null)
	const [resending, setResending] = useState(false)
	const [resendResult, setResendResult] = useState<InvitationResult | null>(null)
	const [resendError, setResendError] = useState('')
	const navigate = useNavigate()
	const brokers = useBrokers()
	const { token, isVerifying } = useSessionTokenVerification({
		mode: 'require',
		redirectTo: '/form-admin-connection',
	})

	// Fetch forms on component mount
	useEffect(() => {
		if (!token) return

		const fetchForms = async () => {
			try {
				const response = await brokers.client.brokerListForms({ token })
				setForms(response.forms)
			} catch (error) {
				console.error('Error fetching forms: ', error)
			}
		}
		fetchForms()
	}, [brokers.client, token])

	// Load the broker's recently opened forms from the backend.
	useEffect(() => {
		if (!token) return

		const fetchRecentForms = async () => {
			try {
				const response = await brokers.client.listRecentForms({ token })
				setRecentForms(response.forms)
			} catch (error) {
				console.error('Error fetching recent forms: ', error)
			}
		}
		fetchRecentForms()
	}, [brokers.client, token])

	const trimmedQuery = query.trim()
	const isSearching = trimmedQuery !== ''

	const filteredForms = useMemo(
		() => forms.filter(form => matchesQuery(form, query)),
		[forms, query],
	)

	// Record that the broker opened a form, then navigate to it.
	const openForm = async (form: FormSubmissionSummary) => {
		if (token) {
			try {
				await brokers.client.recordFormView({ token, formId: form.id })
			} catch (error) {
				console.error('Error recording form view: ', error)
			}
		}
		navigate(`/form?token=${form.shareToken}`)
	}

	const copyFormLink = (shareToken: string) =>
		navigator.clipboard.writeText(`${window.location.origin}/form?token=${shareToken}`)

	const formLink = (shareToken: string) => `${window.location.origin}/form?token=${shareToken}`

	const confirmResend = async () => {
		if (!token || resendTarget === null) return
		setResending(true)
		setResendError('')
		try {
			const response = await brokers.client.resendFormInvitation({
				token,
				formId: resendTarget.id,
			})
			setResendResult({
				link: formLink(response.shareToken),
				emailedTo: response.emailedTo,
				emailFailures: response.emailFailures,
			})
			setResendTarget(null)
		} catch (error) {
			console.error('Error resending the form invitation: ', error)
			// The back-end caps resends per form; say so rather than reporting it as
			// a generic failure the broker would just retry.
			setResendError(
				ConnectError.from(error).code === Code.ResourceExhausted
					? 'Ce lien a déjà été renvoyé plusieurs fois. Réessayez dans une heure.'
					: "Une erreur est survenue lors de l'envoi des emails.",
			)
		} finally {
			setResending(false)
		}
	}

	const renderTile = (form: FormSubmissionSummary) => (
		<Tile
			key={form.id.toString()}
			name={form.projectName}
			city={form.city}
			buyer={form.buyerLastName + ' ' + form.buyerFirstName}
			status={form.status}
			onClick={() => openForm(form)}
			onCopyLink={() => copyFormLink(form.shareToken)}
			onResendEmail={() => {
				setResendError('')
				setResendTarget(form)
			}}
		/>
	)

	if (isVerifying || !token) return <BrandedLoader />

	return (
		<Layout searchValue={query} onSearchChange={setQuery} onSearchSubmit={setQuery}>
			{isSearching ? (
					<>
						<p className='searchResultsCount'>
							{filteredForms.length}{' '}
							{filteredForms.length <= 1 ? 'résultat trouvé' : 'résultats trouvés'} pour la
							recherche «&nbsp;{trimmedQuery}&nbsp;»
						</p>
						<section className='panel searchResultsPanel'>
							{filteredForms.length > 0 ? (
								<div className='panelTiles searchResultsGrid'>
									{filteredForms.map(renderTile)}
								</div>
							) : (
								<p className='panelEmpty'>Aucun résultat pour cette recherche.</p>
							)}
						</section>
					</>
				) : forms.length === 0 ? (
					<EmptyFormsCard onClick={() => navigate('/form-creation')} />
				) : (
					<div className='formAdminGrid'>
						<div className='formAdminLeft'>
							<CreateFormCard onClick={() => navigate('/form-creation')} />
							<section className='panel'>
								<h2 className='panelTitle'>Dernières recherches</h2>
								{recentForms.length > 0 ? (
									<div className='panelTiles formsGrid'>{recentForms.map(renderTile)}</div>
								) : (
									<p className='panelEmpty'>Aucune recherche récente.</p>
								)}
							</section>
						</div>
						<section className='panel formAdminRight'>
							<h2 className='panelTitle'>Derniers formulaires</h2>
							{forms.length > 0 ? (
								<div className='panelTiles formsGrid'>{forms.map(renderTile)}</div>
							) : (
								<p className='panelEmpty'>Aucun formulaire pour le moment.</p>
							)}
						</section>
					</div>
				)}
			{resendTarget !== null && (
				<Modal>
					<div className='modalBody'>
						<h1>Renvoyer le lien par email ?</h1>
						<p>
							Le lien du formulaire «&nbsp;{resendTarget.projectName}&nbsp;» va être renvoyé
							par email à {resendTarget.buyerFirstName} {resendTarget.buyerLastName} et à ses
							co-acquéreurs.
						</p>
						{resendError !== '' && <p className='resendError'>{resendError}</p>}
						<div className='modalActions'>
							<Button onClick={() => setResendTarget(null)} disabled={resending}>
								Annuler
							</Button>
							<Button onClick={confirmResend} loading={resending}>
								{resending ? 'Envoi en cours...' : 'Envoyer'}
							</Button>
						</div>
					</div>
				</Modal>
			)}
			{resendResult !== null && (
				<InvitationResultModal
					title={
						resendResult.emailedTo.length > 0
							? 'Le lien a bien été renvoyé'
							: "Le lien n'a pas pu être renvoyé"
					}
					result={resendResult}
					closeLabel='Fermer'
					onClose={() => setResendResult(null)}
				/>
			)}
		</Layout>
	)
}

export default FormAdmin
